import React from "react";
import { Link } from "react-router-dom";
import { Bowtie, CheckIcon } from "./icons";

const Pillar = ({ letter, label }) => (
  <div className="flex items-center gap-3">
    <span className="w-9 h-9 rounded-full border border-gold/40 flex items-center justify-center font-display text-gold text-lg">
      {letter}
    </span>
    <span className="font-display text-base md:text-lg tracking-wide text-white/85">{label}</span>
  </div>
);

const BULLETS = [
  "How to turn a passive site into a client-booking engine",
  "The 3 systems every revenue site needs",
  "A live walkthrough of the exact funnel structure we use",
];

export default function FinalCTA({ onJoinNow, onRegisterLater, linkTo }) {
  // When linkTo is provided, the CTA buttons navigate to that route instead of opening the registration modal.
  // This lets product pages (workshop / vault / viral / ebook) show the same block while keeping the popup
  // exclusive to the home + funnels pages.
  const useLinks = Boolean(linkTo);

  return (
    <section data-testid="final-cta" className="relative py-24 md:py-32">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div
          className="relative rounded-3xl overflow-hidden p-8 md:p-16 border border-gold/30"
          style={{
            background:
              "radial-gradient(ellipse at top right, rgba(212,175,55,0.12), transparent 60%), linear-gradient(135deg, #0c0c0c 0%, #050505 100%)",
          }}
        >
          {/* Geometric gold lines */}
          <svg
            className="absolute inset-0 w-full h-full opacity-30 pointer-events-none"
            viewBox="0 0 1200 600"
            preserveAspectRatio="none"
          >
            <line x1="0" y1="100" x2="1200" y2="0" stroke="#D4AF37" strokeWidth="0.5" />
            <line x1="0" y1="500" x2="1200" y2="600" stroke="#D4AF37" strokeWidth="0.5" />
            <line x1="200" y1="0" x2="0" y2="600" stroke="#D4AF37" strokeWidth="0.5" />
            <line x1="1000" y1="0" x2="1200" y2="600" stroke="#D4AF37" strokeWidth="0.5" />
          </svg>

          <div className="relative z-10 text-center max-w-3xl mx-auto">
            <span
              data-testid="masterclass-free-badge"
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full mb-5 text-[10px] uppercase tracking-[0.32em] font-bold"
              style={{
                background: "linear-gradient(135deg,#f3d172 0%,#D4AF37 60%,#a07e1f 100%)",
                color: "#0a0a0a",
                boxShadow: "0 0 18px rgba(212,175,55,0.45)",
              }}
            >
              ★ 100% Free ★
            </span>
            <br />
            <span className="text-[11px] uppercase tracking-[0.28em] text-gold/90">
              Free Automated Masterclass
            </span>
            <h2 className="mt-5 font-display text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
              Ready To Build A<br />
              <span className="bk-gold-text italic">Revenue System?</span>
            </h2>

            <p className="mt-6 text-white/70 text-base md:text-lg leading-relaxed max-w-xl mx-auto">
              Discover the clarity, momentum, and systems you need to turn your website
              into a client-booking engine.
            </p>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-6 md:gap-10">
              <Pillar letter="C" label="CLARITY" />
              <Pillar letter="M" label="MOMENTUM" />
              <Pillar letter="S" label="SYSTEMS" />
            </div>

            <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
              {useLinks ? (
                <Link
                  to={linkTo}
                  data-testid="final-join-now"
                  className="inline-flex items-center justify-center gap-2 px-9 py-4 rounded-full bk-shimmer text-black text-xs uppercase tracking-[0.24em] font-bold hover:scale-[1.03] transition-transform min-w-[220px]"
                >
                  <Bowtie className="w-4 h-4" />
                  Join Now
                </Link>
              ) : (
                <button
                  onClick={onJoinNow}
                  data-testid="final-join-now"
                  className="inline-flex items-center justify-center gap-2 px-9 py-4 rounded-full bk-shimmer text-black text-xs uppercase tracking-[0.24em] font-bold hover:scale-[1.03] transition-transform min-w-[220px]"
                >
                  <Bowtie className="w-4 h-4" />
                  Join Now
                </button>
              )}
              {useLinks ? (
                <Link
                  to={linkTo}
                  data-testid="final-register-later"
                  className="inline-flex items-center justify-center gap-2 px-9 py-4 rounded-full border border-gold/40 text-white text-xs uppercase tracking-[0.24em] font-bold hover:border-gold hover:bg-gold/10 transition-colors min-w-[220px]"
                >
                  Register For Later
                </Link>
              ) : (
                <button
                  onClick={onRegisterLater}
                  data-testid="final-register-later"
                  className="inline-flex items-center justify-center gap-2 px-9 py-4 rounded-full border border-gold/40 text-white text-xs uppercase tracking-[0.24em] font-bold hover:border-gold hover:bg-gold/10 transition-colors min-w-[220px]"
                >
                  Register For Later
                </button>
              )}
            </div>

            <ul className="mt-10 grid sm:grid-cols-3 gap-3 max-w-3xl mx-auto text-left">
              {BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm text-white/70 bk-card rounded-xl px-4 py-3">
                  <CheckIcon className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 text-white/40 text-xs">
              Scheduled training · Reserve your spot · Confirmation by email
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
